// src/lib/trackizy.js
// Unified API client for the Trackizy web SPA.
//
// This file is a thin facade over @trackizy/api-client for the typed core
// groups (auth, devices, positions, rules, chat, reports,
// branding, users, orgs, storage, snap, broadcasts, musters, incidents). The remaining
// web-only groups (geofences, drivers, pois, scheduledReports, sultan API
// tokens, etc.) stay here as plain JS helpers that route through
// client.request() — they'll migrate to @trackizy/api-client incrementally
// as the mobile app grows to need them. Zero call-site changes required.

import { createTabTokenStorage } from './tabTokenStorage';
import { createClient } from '@trackizy/api-client';
import { createWebTokenStorage } from '@trackizy/api-client/storage/web';
import i18n from './i18n';
import { queryString } from './queryString';

// The token-storage adapter. Named `tokenStorage` so the public `storage`
// export below can keep its historical meaning (file uploads).
const tokenStorage = createTabTokenStorage(createWebTokenStorage());

const client = createClient({ baseUrl: '/api', storage: tokenStorage });

// === Instance-role error translation ======================================
// Support-role gaps (a support user missing a capability) come back from the
// server as `ApiError` with a stable `.code` and an English `.message`. Every
// `showToast(err?.message)` site across the app should show a translated,
// actionable message instead — this is the single place that rewrites those
// two codes before the error reaches any call site. Pure so it's testable
// without a live client (see trackizy.test.js).
export function translateApiError(err) {
  if (err && err.code === 'support_no_delete') {
    err.message = i18n.t(
      'instance.errors.supportNoDelete',
      'Your support role cannot delete. Ask an Instance Admin for the "delete" capability.',
    );
  } else if (err && err.code === 'capability_required') {
    err.message = i18n.t(
      'instance.errors.capabilityRequired',
      'This action needs a capability you do not have. Ask an Instance Admin.',
    );
  } else if (err && err.code === 'imei_exists') {
    // Device create/edit with a tracker identifier already on the box (409).
    err.message = i18n.t(
      'settings.deviceImeiExists',
      'A vehicle with this identifier (IMEI) already exists on this server.',
    );
  } else if (err && err.code === 'primary_org') {
    // DELETE /api/orgs/:id on the instance's primary organization (409).
    err.message = i18n.t(
      'sultan.primaryOrgDeleteBlocked',
      'This is the primary organization. Mark another organization as primary before deleting it.',
    );
  } else if (err && err.code === 'device_rejected') {
    // The tracking service refused the device write (400); the server never passes its reason on.
    err.message = i18n.t('settings.deviceRejected', 'The tracking service rejected this vehicle.');
  }
  return err;
}

function withErrorTranslation(fn) {
  return function translatedRequest(...args) {
    const result = fn(...args);
    if (result && typeof result.then === 'function') {
      return result.catch((err) => {
        throw translateApiError(err);
      });
    }
    return result;
  };
}

// A namespace value we're willing to walk into: a plain `{...}` bag of
// endpoint functions, not an array / class instance / null.
function isPlainObject(value) {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Wrap every function on a namespace so its rejections pass through
 * `translateApiError`. Recurses ONE level into plain-object properties so
 * sub-namespaces (`orgs.members.remove`, `users.pushTokens.*`,
 * `rules.alerts.*`, `devices.photos.*`, …) are covered too. Anything that is
 * neither a function nor a plain object is copied through untouched.
 * Exported for the test.
 */
export function wrapNamespace(ns, depth = 1) {
  const wrapped = {};
  for (const key of Object.keys(ns)) {
    const value = ns[key];
    if (typeof value === 'function') wrapped[key] = withErrorTranslation(value);
    else if (depth > 0 && isPlainObject(value)) wrapped[key] = wrapNamespace(value, depth - 1);
    else wrapped[key] = value;
  }
  return wrapped;
}

// EVERY namespace the client exports goes through the translator — not a
// hand-maintained list, which silently left new namespaces (and every nested
// sub-namespace) untranslated. Top-level functions are left alone here:
// `setBaseUrl`/`getBaseUrl` are synchronous and wrapping them would turn them
// into promises. `request()` — used by the web-only helpers below — is wrapped
// explicitly.
for (const key of Object.keys(client)) {
  if (isPlainObject(client[key])) client[key] = wrapNamespace(client[key]);
}
client.request = withErrorTranslation(client.request);

// === Auth-token helpers ==================================================
// Web callers rely on `getAuthToken()` returning synchronously (string|null).
// The web adapter is sync, so we unwrap the promise-typed interface safely.

export function getAuthToken() {
  const v = tokenStorage.get();
  return typeof v === 'string' || v === null ? v : null;
}

export function setAuthToken(token, opts) {
  tokenStorage.set(token, opts);
}

// === Core typed groups (re-exported from @trackizy/api-client) ===========
// Keep the public `storage` name referring to client.storage (file upload API).
export const {
  auth,
  devices,
  positions,
  rules,
  chat,
  reports,
  branding,
  users,
  orgs,
  storage,
  request,
  snap,
  broadcasts,
  musters,
  incidents,
  assure,
} = client;

export default client;

// === Web-only helpers (not yet ported to @trackizy/api-client) ==========
// All use client.request under the hood — same headers, same 401 handling,
// same JSON body convention as the old local request().

// Arrays go out comma-joined (`deviceId=1,2`), never as repeated keys — see queryString.
function qs(params) {
  const q = queryString(params);
  return q ? `?${q}` : '';
}

// === Maintenance plans (spec 2026-10-14-maintenance-plans) ================
// All status comes from the server; the browser never computes due/overdue.
export const maintenancePlans = {
  list: () => client.request('/maintenance/plans'),
  create: (body) => client.request('/maintenance/plans', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/maintenance/plans/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/maintenance/plans/${id}`, { method: 'DELETE' }),
  status: (params) => client.request(`/maintenance/status${qs(params)}`),
  setBaseline: (planId, deviceId, body) =>
    client.request(`/maintenance/plans/${planId}/vehicles/${deviceId}/baseline`, { method: 'POST', body: JSON.stringify(body) }),
  recordEvent: (body) => client.request('/maintenance/events', { method: 'POST', body: JSON.stringify(body) }),
  events: (params) => client.request(`/maintenance/events${qs(params)}`),
};

function crud(basePath) {
  return {
    list: (params) => client.request(`${basePath}${qs(params)}`),
    get: (id) => client.request(`${basePath}/${id}`),
    create: (data) =>
      client.request(basePath, { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) =>
      client.request(`${basePath}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => client.request(`${basePath}/${id}`, { method: 'DELETE' }),
  };
}

// --- Custom field definitions (org-admin schema) ---
export const customFields = {
  ...crud('/custom-fields'),
  reorder: (orderedIds) =>
    client.request('/custom-fields/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ order: orderedIds }),
    }),
};

// --- Per-device custom-field values (PATCH; null clears a key) ---
export const deviceCustomFields = {
  update: (deviceId, values) =>
    client.request(`/devices/${deviceId}/custom-fields`, {
      method: 'PATCH',
      body: JSON.stringify(values),
    }),
};

// --- Multi-tracker vehicles (group 2-4 trackers into one car; migration 065) ---
// update is PATCH (partial metadata edit); membership + primary are dedicated verbs.
export const vehicles = {
  list: (params) => client.request(`/vehicles${qs(params)}`),
  get: (id) => client.request(`/vehicles/${id}`),
  create: (data) =>
    client.request('/vehicles', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    client.request(`/vehicles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => client.request(`/vehicles/${id}`, { method: 'DELETE' }),
  addTracker: (id, deviceId) =>
    client.request(`/vehicles/${id}/trackers`, {
      method: 'POST',
      body: JSON.stringify({ deviceId }),
    }),
  removeTracker: (id, deviceId) =>
    client.request(`/vehicles/${id}/trackers/${encodeURIComponent(deviceId)}`, {
      method: 'DELETE',
    }),
  setPrimary: (id, deviceId) =>
    client.request(`/vehicles/${id}/primary`, {
      method: 'POST',
      body: JSON.stringify({ deviceId }),
    }),
};

// --- Traccar-proxied CRUD groups ---
export const geofences = crud('/geofences');
export const groups = crud('/groups');
export const drivers = crud('/drivers');
export const calendars = crud('/calendars');

// --- Commands ---
export const commands = {
  ...crud('/commands'),
  send: (data) =>
    client.request('/commands/send', { method: 'POST', body: JSON.stringify(data) }),
  types: (deviceId) =>
    client.request(`/commands/types${deviceId ? `?deviceId=${deviceId}` : ''}`),
};

// --- Permissions (Traccar link/unlink) ---
export const permissions = {
  link: (data) =>
    client.request('/permissions', { method: 'POST', body: JSON.stringify(data) }),
  unlink: (data) =>
    client.request('/permissions', { method: 'DELETE', body: JSON.stringify(data) }),
};

// --- Traccar server config ---
export const serverConfig = {
  get: () => client.request('/server'),
  update: (data) =>
    client.request('/server', { method: 'PUT', body: JSON.stringify(data) }),
};

// --- Terminal (device connection log) ---
export const terminal = {
  history: (params = {}) => client.request(`/terminal/history${qs(params)}`),
  unknownAttempts: (params = {}) =>
    client.request(`/terminal/unknown-attempts${qs(params)}`),
  dismissUnknown: (id) =>
    client.request(`/terminal/unknown-attempts/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
    }),
};

// --- Computed attributes ---
export const computedAttributes = {
  ...crud('/attributes/computed'),
  test: (deviceId, data) =>
    client.request(`/attributes/computed/test?deviceId=${deviceId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// --- POIs ---
export const pois = crud('/pois');
export const poiCategories = crud('/pois/categories');

// --- Scheduled reports ---
export const scheduledReports = {
  ...crud('/scheduled-reports'),
  runs: (scheduleId) => client.request(`/scheduled-reports/${scheduleId}/runs`),
};

// --- Notification rules (alias surface for legacy call sites) ---
// The typed `rules` group above already covers CRUD + toggle + events; this
// local binding exists so every legacy import { notificationRules } keeps
// resolving. Exposes the historical method names (upsert, toggle).
export const notificationRules = {
  ...crud('/rules'),
  upsert: (data) =>
    client.request('/rules', { method: 'POST', body: JSON.stringify(data) }),
  toggle: (id, enabled) =>
    client.request(`/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
  dryRun: (id, body) =>
    client.request(`/rules/${id}/dry-run`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  swapRecipient: (body) =>
    client.request('/rules/bulk/swap-recipient', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeRecipient: (body) =>
    client.request('/rules/bulk/remove-recipient', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// --- Rule events (alert history) ---
export const ruleEvents = {
  list: (params) => client.request(`/rules/events${qs(params)}`),
  acknowledge: (ids) =>
    client.request('/rules/events/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  loadMore: (params) => client.request(`/rules/events${qs(params)}`),
};

// --- Diagnostics (admin connectivity / health dashboard) ---
export const diagnostics = {
  connectivity: (params) => client.request(`/diagnostics/connectivity${qs(params)}`),
  sensors: (params) => client.request(`/diagnostics/sensors${qs(params)}`),
};

// --- Activity log (admin audit trail) ---
export const activity = {
  list: (params) => client.request(`/activity${qs(params)}`),
  facets: () => client.request('/activity/facets'),
};

// --- Expiry (subscription expiry tracking) ---
export const expiry = {
  // Admin / org-scoped
  summary: () => client.request('/expiry/summary'),
  // Sultan-only
  sultanOrgs: () => client.request('/expiry/sultan/orgs'),
  sultanUnits: (params) => client.request(`/expiry/sultan/units${qs(params)}`),
  sultanConfig: () => client.request('/expiry/sultan/config'),
  sultanUpdateConfig: (body) =>
    client.request('/expiry/sultan/config', { method: 'PATCH', body: JSON.stringify(body) }),
  sultanUpdateOrg: (id, body) =>
    client.request(`/expiry/sultan/orgs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  sultanUpdateUnit: (deviceId, body) =>
    client.request(`/expiry/sultan/units/${deviceId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  sultanPeople: (params) => client.request(`/expiry/sultan/people${qs(params)}`),
  sultanUpdatePerson: (userId, body) =>
    client.request(`/expiry/sultan/people/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

// --- Analytics (fire-and-forget; swallow errors) ---
export const analytics = {
  track: (events) =>
    client
      .request('/analytics/events', {
        method: 'POST',
        body: JSON.stringify(events),
      })
      .catch(() => {}),
};

// --- Safety: SOS ---
export const sos = {
  list: () => client.request('/safety/sos'),
  trigger: (data = {}) =>
    client.request('/safety/sos', { method: 'POST', body: JSON.stringify(data) }),
  acknowledge: (id) =>
    client.request(`/safety/sos/${id}/acknowledge`, { method: 'PUT' }),
};

// --- Safety: check-ins ---
export const checkins = {
  events: (params) => client.request(`/safety/checkins${qs(params)}`),
  config: {
    list: () => client.request('/safety/checkins/config'),
    upsert: (data) =>
      client.request('/safety/checkins/config', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id) =>
      client.request(`/safety/checkins/config/${id}`, { method: 'DELETE' }),
  },
};

// School Bus module — parent links. `public` needs no token; client.request
// already sends no Authorization header when none is stored.
export const busLinks = {
  // `open: true` only on a page's first poll — the server counts it once in bus_link_opens.
  public: (token, { open = false } = {}) => client.request(`/public/bus/${encodeURIComponent(token)}${open ? '?open=1' : ''}`),
  list: () => client.request('/bus-links'),
  byDevice: (deviceId) => client.request(`/bus-links/by-device/${deviceId}`),
  create: (data) => client.request('/bus-links', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => client.request(`/bus-links/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  regenerate: (id) => client.request(`/bus-links/${id}/regenerate`, { method: 'POST' }),
  remove: (id) => client.request(`/bus-links/${id}`, { method: 'DELETE' }),
  // Driver roll-call link (School Bus Standard): issue/rotate returns { url } ONCE (the earlier link and the PIN stop
  // working); setPin(id, '1234' | null) → 204, 400 invalid_pin, 409 no_link (issue a link first).
  driverLink: {
    issue: (id) => client.request(`/bus-links/${id}/driver-link`, { method: 'POST' }),
    revoke: (id) => client.request(`/bus-links/${id}/driver-link`, { method: 'DELETE' }),
    setPin: (id, pin) => client.request(`/bus-links/${id}/driver-pin`, { method: 'PUT', body: JSON.stringify({ pin: pin || null }) }),
  },
};

// /api/school-bus — School Bus Standard (students + family links). Admin/manager, Standard-gated.
// The family page is public (no session), like busLinks.public.
const sbJson = (method, data) => ({ method, body: JSON.stringify(data ?? {}) });
const drivePin = (pin) => (pin ? { headers: { 'X-Drive-Pin': pin } } : {});
export const schoolBus = {
  students: {
    list: (params) => client.request(`/school-bus/students${qs(params)}`),
    create: (data) => client.request('/school-bus/students', sbJson('POST', data)),
    update: (id, data) => client.request(`/school-bus/students/${id}`, sbJson('PATCH', data)),
    archive: (id) => client.request(`/school-bus/students/${id}/archive`, { method: 'POST' }),
    restore: (id) => client.request(`/school-bus/students/${id}/restore`, { method: 'POST' }),
    // Admin only; the student's name typed back (409 name_mismatch).
    remove: (id, name) => client.request(`/school-bus/students/${id}`, sbJson('DELETE', { name })),
    // Admin only. parentLanguage applies to the students the import creates.
    bulk: (rows, parentLanguage) => client.request('/school-bus/students/bulk', sbJson('POST', parentLanguage ? { rows, parentLanguage } : { rows })),
    undoBatch: (batchId) => client.request(`/school-bus/students/batch/${batchId}`, { method: 'DELETE' }),
    addPhone: (id, data) => client.request(`/school-bus/students/${id}/phones`, sbJson('POST', data)),
    updatePhone: (id, phoneId, data) => client.request(`/school-bus/students/${id}/phones/${phoneId}`, sbJson('PATCH', data)),
    removePhone: (id, phoneId) => client.request(`/school-bus/students/${id}/phones/${phoneId}`, { method: 'DELETE' }),
    // 202; 429 resend_cooldown carries retryAfterSec in the error body.
    resend: (id, phoneId) => client.request(`/school-bus/students/${id}/phones/${phoneId}/resend`, { method: 'POST' }),
    // Printable link, no SMS: rotates the number's token (the link sent earlier stops working) and returns { url } once.
    link: (id, phoneId) => client.request(`/school-bus/students/${id}/phones/${phoneId}/link`, { method: 'POST' }),
  },
  // Stops are org-wide; archive is 409 stop_in_use while a route or student uses the stop.
  stops: {
    list: (params) => client.request(`/school-bus/stops${qs(params)}`),
    create: (data) => client.request('/school-bus/stops', sbJson('POST', data)),
    update: (id, data) => client.request(`/school-bus/stops/${id}`, sbJson('PATCH', data)),
    archive: (id) => client.request(`/school-bus/stops/${id}/archive`, { method: 'POST' }),
    // [{ lat, lng, days, firstSeen, lastSeen }] — halts this bus repeats, away from the school and existing stops.
    suggested: (deviceId) => client.request(`/school-bus/stops/suggested${qs({ deviceId })}`),
  },
  // One route per bus and direction ('morning' | 'afternoon'); PUT replaces the whole route.
  routes: {
    list: () => client.request('/school-bus/routes'),
    get: (deviceId, direction) => client.request(`/school-bus/routes/${deviceId}/${direction}`),
    put: (deviceId, direction, data) => client.request(`/school-bus/routes/${deviceId}/${direction}`, sbJson('PUT', data)),
    remove: (deviceId, direction) => client.request(`/school-bus/routes/${deviceId}/${direction}`, { method: 'DELETE' }),
  },
  runs: {
    list: (day) => client.request(`/school-bus/runs${qs({ day })}`),
    get: (id) => client.request(`/school-bus/runs/${id}`),
    end: (id) => client.request(`/school-bus/runs/${id}/end`, { method: 'POST' }),
    // Cancel tells every family on the bus (409 already_ended). cancelPlanned cancels a direction with no run yet
    // ({ deviceId, direction, day?, reason? } → 201 { runId }, 409 exists). Neither can be undone.
    cancel: (id, reason) => client.request(`/school-bus/runs/${id}/cancel`, sbJson('POST', reason ? { reason } : {})),
    cancelPlanned: (body) => client.request('/school-bus/runs/cancel', sbJson('POST', body)),
    // Office status (source = office): 'pending' | 'boarded' | 'dropped' | 'absent' → the updated row.
    setStudentStatus: (runId, studentId, status) => client.request(`/school-bus/runs/${runId}/students/${studentId}`, sbJson('PATCH', { status })),
  },
  // { buses, attention } for one org-timezone day.
  today: {
    get: (day) => client.request(`/school-bus/today${qs({ day })}`),
  },
  // The parent SMS ledger (bus_notifications). resend → 409 not_failed unless the row failed / had no channel.
  messages: {
    list: (params) => client.request(`/school-bus/notifications${qs(params)}`),
    resend: (id) => client.request(`/school-bus/notifications/${id}/resend`, { method: 'POST' }),
  },
  family: {
    public: (token, { open = false } = {}) => client.request(`/public/family/${encodeURIComponent(token)}${open ? '?open=1' : ''}`),
  },
  // Driver roll-call page (/drive/:token), public. The bus PIN, when set, rides every call as
  // X-Drive-Pin; a wrong/missing one is a 403 (never a 401, so no stored session is touched).
  drive: {
    get: (token, { direction, pin } = {}) =>
      client.request(`/public/drive/${encodeURIComponent(token)}${qs(direction ? { direction } : {})}`, drivePin(pin)),
    tap: (token, body, pin) => client.request(`/public/drive/${encodeURIComponent(token)}/tap`, { ...sbJson('POST', body), ...drivePin(pin) }),
    done: (token, pin) => client.request(`/public/drive/${encodeURIComponent(token)}/done`, { ...sbJson('POST', {}), ...drivePin(pin) }),
  },
};

// /api/cold-chain — Cold Chain module (TASK-382, spec docs/superpowers/specs/2026-10-04-cold-chain-phase1-design.md §8).
export const coldChain = {
  overview: () => client.request('/cold-chain/overview'),
  devicePoints: (deviceId) => client.request(`/cold-chain/points${qs({ deviceId })}`),
  createPoint: (data) => client.request('/cold-chain/points', { method: 'POST', body: JSON.stringify(data) }),
  updatePoint: (id, data) => client.request(`/cold-chain/points/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  archivePoint: (id, data) => client.request(`/cold-chain/points/${id}/archive`, { method: 'POST', body: JSON.stringify(data ?? {}) }),
  versions: (id) => client.request(`/cold-chain/points/${id}/versions`),
  excludedReadings: (params) => client.request(`/cold-chain/report/excluded${qs(params)}`),
  copyPoints: (data) => client.request('/cold-chain/points/copy', { method: 'POST', body: JSON.stringify(data) }),
  setDeviceMode: (deviceId, data) => client.request(`/cold-chain/devices/${deviceId}`, { method: 'PUT', body: JSON.stringify(data) }),
  history: (id, params) => client.request(`/cold-chain/points/${id}/history${qs(params)}`),
  excursions: (params) => client.request(`/cold-chain/excursions${qs(params)}`),
  report: (params) => client.request(`/cold-chain/report${qs(params)}`),
  enableAlarms: () => client.request('/cold-chain/alarms', { method: 'POST' }),
  // Sites and Route Replay colouring (spec 2026-10-10-cold-chain-replay-and-sites-design.md §5).
  sites: () => client.request('/cold-chain/sites'),
  createSite: (data) => client.request('/cold-chain/sites', { method: 'POST', body: JSON.stringify(data) }),
  updateSite: (id, data) => client.request(`/cold-chain/sites/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  archiveSite: (id) => client.request(`/cold-chain/sites/${id}/archive`, { method: 'POST' }),
  replay: (params) => client.request(`/cold-chain/replay${qs(params)}`),
  // Sealed records (spec 2026-10-06-cold-chain-sealed-record-design.md §7).
  issueSeal: (data) => client.request('/cold-chain/seals', { method: 'POST', body: JSON.stringify(data), timeoutMs: 120_000 }),
  registerSealPdf: (id, sha256) => client.request(`/cold-chain/seals/${id}/pdf`, { method: 'POST', body: JSON.stringify({ sha256 }) }),
  seals: (params) => client.request(`/cold-chain/seals${qs(params)}`),
  seal: (id) => client.request(`/cold-chain/seals/${id}`),
  verifySeal: (id) => client.request(`/cold-chain/seals/${id}/verify`, { timeoutMs: 120_000 }),
  revokeSeal: (id, data) => client.request(`/cold-chain/seals/${id}/revoke`, { method: 'POST', body: JSON.stringify(data) }),
};

// /api/fuel — Fuel module (TASK-445, spec docs/superpowers/specs/2026-10-13-fuel-module-phase1-design.md §7).
export const fuel = {
  // params: { suggest: 1 } only from the Setup tab — adds the per-device "suggested" probe (spec §7)
  overview: (params) => client.request(`/fuel/overview${qs(params)}`),
  consumptionRows: (params) => client.request(`/fuel/consumption/rows${qs(params)}`),
  purchaseRows: (params) => client.request(`/fuel/purchases/rows${qs(params)}`),
  purchases: (params) => client.request(`/fuel/purchases${qs(params)}`),
  createPurchase: (body) => client.request('/fuel/purchases', { method: 'POST', body: JSON.stringify(body) }),
  updatePurchase: (id, body) => client.request(`/fuel/purchases/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePurchase: (id) => client.request(`/fuel/purchases/${id}`, { method: 'DELETE' }),
  importPurchases: (rows) => client.request('/fuel/purchases/bulk', { method: 'POST', body: JSON.stringify({ rows }) }),
  undoImport: (batchId) => client.request(`/fuel/purchases/batch/${batchId}`, { method: 'DELETE' }),
  variance: (params) => client.request(`/fuel/purchases/variance${qs(params)}`),
  presets: () => client.request('/fuel/presets').then((r) => r?.presets ?? []),
  profiles: () => client.request('/fuel/profiles').then((r) => r?.profiles ?? []),
  profile: (id) => client.request(`/fuel/profiles/${id}`),
  createProfile: (data) => client.request('/fuel/profiles', { method: 'POST', body: JSON.stringify(data) }),
  updateProfile: (id, data) => client.request(`/fuel/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  archiveProfile: (id) => client.request(`/fuel/profiles/${id}`, { method: 'DELETE' }),
  copyProfile: (data) => client.request('/fuel/profiles/copy', { method: 'POST', body: JSON.stringify(data) }),
  rerun: (id, days) => client.request(`/fuel/profiles/${id}/rerun`, { method: 'POST', body: JSON.stringify({ days }) }),
  rerunStatus: (id) => client.request(`/fuel/profiles/${id}/rerun`),
  events: (params) => client.request(`/fuel/events${qs(params)}`),
  setEventNote: (id, note) => client.request(`/fuel/events/${id}`, { method: 'PATCH', body: JSON.stringify({ note }) }),
  history: (params) => client.request(`/fuel/history${qs(params)}`),
  prices: () => client.request('/fuel/prices').then((r) => r?.prices ?? []),
  consumption: (params) => client.request(`/fuel/consumption${qs(params)}`),
  consumptionDaily: (deviceId, params) => client.request(`/fuel/consumption/${deviceId}/daily${qs(params)}`).then((r) => r?.days ?? []),
  recomputeConsumption: (data) => client.request('/fuel/consumption/recompute', { method: 'POST', body: JSON.stringify(data) }),
};

// /api/public/seal — anonymous verification of a sealed cold chain record by its code.
export const publicSeal = {
  lookup: (code) => client.request(`/public/seal/${encodeURIComponent(code)}`),
  check: async (code, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`/api/public/seal/${encodeURIComponent(code)}/check`, { method: 'POST', body: fd });
    if (!r.ok) throw Object.assign(new Error('check_failed'), { status: r.status });
    return r.json();
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Work unification (Plan 3a) — net-new namespaces over the unified server
// surface.
// ─────────────────────────────────────────────────────────────────────────────

// /api/work — unified replacement for jobs + tasks + routes.
export const work = {
  list: (params) => client.request(`/work${qs(params)}`),
  get: (id) => client.request(`/work/${id}`),
  create: (data) =>
    client.request('/work', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    client.request(`/work/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => client.request(`/work/${id}`, { method: 'DELETE' }),

  setStatus: (id, body) =>
    client.request(`/work/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  setShare: (id, body) =>
    client.request(`/work/${id}/share`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // Mapbox Optimization round-trip — routinely exceeds the 15s default deadline.
  // `criterion` is REQUIRED by the route (400 without it). Posting no body at
  // all is why Optimize never worked — see features/work/optimizeContract.js.
  // Responds { work, distanceMeters, durationSeconds, geometry }, not a work row.
  optimize: (id, criterion) =>
    client.request(`/work/${id}/optimize`, {
      method: 'POST',
      timeoutMs: 60_000,
      body: JSON.stringify({ criterion }),
    }),

  // Public — no auth header injected because client.request already handles
  // the no-token case for share endpoints.
  share: (token) => client.request(`/work/share/${token}`),

  stops: {
    list: (workId) => client.request(`/work/${workId}/stops`),
    add: (workId, data) =>
      client.request(`/work/${workId}/stops`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (workId, stopId, data) =>
      client.request(`/work/${workId}/stops/${stopId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    setStatus: (workId, stopId, body) =>
      client.request(`/work/${workId}/stops/${stopId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (workId, stopId) =>
      client.request(`/work/${workId}/stops/${stopId}`, { method: 'DELETE' }),
    // Server expects body.stopIds — see work-stops.routes.ts. Keep the
    // helper's parameter name for caller readability, just rename in the wire body.
    reorder: (workId, orderedStopIds) =>
      client.request(`/work/${workId}/stops/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ stopIds: orderedStopIds }),
      }),
  },
};

// /api/forms — renamed from /api/inspections (templates only).
export const forms = {
  list: (params) => client.request(`/forms${qs(params)}`),
  get: (id) => client.request(`/forms/${id}`),
  create: (data) =>
    client.request('/forms', { method: 'POST', body: JSON.stringify(data) }),
  // Server uses PUT for full update (matches dispatch routes pattern).
  update: (id, data) =>
    client.request(`/forms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => client.request(`/forms/${id}`, { method: 'DELETE' }),
};

// /api/form-submissions — unified polymorphism (work | work_stop | neither).
export const formSubmissions = {
  list: (params) => client.request(`/form-submissions${qs(params)}`),
  get: (id) => client.request(`/form-submissions/${id}`),
  create: (data) =>
    client.request('/form-submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// /api/work-templates — org-scoped templates that materialize stops +
// form-requirements when applied via POST /api/work { templateId }.
export const workTemplates = crud('/work-templates');

// /api/work/:id/form-requirements — declarative gating list. Bulk-replace via PATCH.
// The server accepts { workLevelFormIds: string[], stopRequirements?: [{stopId, formIds}] }.
// Plan 3c only sends workLevelFormIds; per-stop forms come in Plan 3d.
export const formRequirements = {
  list: (workId) => client.request(`/work/${workId}/form-requirements`),
  replace: (workId, body) =>
    client.request(`/work/${workId}/form-requirements`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// --- AI (server registers at /intelligence prefix) ---
export const ai = {
  digests: () => client.request('/intelligence/digests'),
  // LLM round-trip — routinely exceeds the 15s default deadline.
  query: (data) =>
    client.request('/intelligence/query', {
      method: 'POST',
      body: JSON.stringify(data),
      timeoutMs: 120_000,
    }),
};

// --- User preferences (saved views, pinned vehicles, dashboard layout) ---
// Legacy top-level binding; api-client also exposes users.preferences.
export const userPreferences = {
  get: (key) => client.request(`/user-preferences/${encodeURIComponent(key)}`),
  set: (key, value) =>
    client.request(`/user-preferences/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),
};

// --- Widgets (Display Wall spec 1): one batched data call per refresh ---
export const widgets = {
  data: (items) => client.request('/widgets/data', { method: 'POST', body: JSON.stringify({ widgets: items }) }),
  catalog: () => client.request('/widgets/catalog'),
};

// --- Walls (Display Wall spec 2): org boards + paired kiosk screens ---
export const walls = {
  list: () => client.request('/walls'),
  get: (id) => client.request(`/walls/${id}`),
  create: (body) => client.request('/walls', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/walls/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  duplicate: (id) => client.request(`/walls/${id}/duplicate`, { method: 'POST' }),
  remove: (id) => client.request(`/walls/${id}`, { method: 'DELETE' }),
  // Public links (spec 3): the token comes back once, from create.
  publicLinks: (id) => client.request(`/walls/${id}/public-links`),
  createPublicLink: (id, body) => client.request(`/walls/${id}/public-links`, { method: 'POST', body: JSON.stringify(body) }),
  revokePublicLink: (id, linkId) => client.request(`/walls/${id}/public-links/${linkId}`, { method: 'DELETE' }),
};
export const wallScreens = {
  list: () => client.request('/wall-screens'),
  claim: (body) => client.request('/wall-screens/claim', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/wall-screens/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/wall-screens/${id}`, { method: 'DELETE' }),
};

// --- Device-to-org mapping (legacy top-level binding) ---
export const deviceOrgMapping = {
  list: (orgId) => client.request(`/orgs/${orgId}/devices`),
  add: (orgId, deviceIds) =>
    client.request(`/orgs/${orgId}/devices`, {
      method: 'POST',
      body: JSON.stringify({ deviceIds }),
    }),
  remove: (orgId, deviceIds) =>
    client.request(`/orgs/${orgId}/devices`, {
      method: 'DELETE',
      body: JSON.stringify({ deviceIds }),
    }),
};

// --- Org members (legacy top-level binding) ---
export const orgMembers = {
  list: (orgId) => client.request(`/orgs/${orgId}/members`),
  add: (orgId, data) =>
    client.request(`/orgs/${orgId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  remove: (orgId, userId) =>
    client.request(`/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),
  resetPassword: (orgId, userId, password) =>
    client.request(`/orgs/${orgId}/members/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
};

// --- App Profiles (Command app screens/actions/prefs governance) ---
export const appProfiles = {
  list:   (orgId, app = 'command') => client.request(`/orgs/${orgId}/app-profiles?app=${app}`),
  create: (orgId, body) => client.request(`/orgs/${orgId}/app-profiles`, { method: 'POST', body: JSON.stringify(body) }),
  update: (orgId, profileId, body) => client.request(`/orgs/${orgId}/app-profiles/${profileId}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (orgId, profileId) => client.request(`/orgs/${orgId}/app-profiles/${profileId}`, { method: 'DELETE' }),
  memberConfig: (orgId, userId, app = 'command') => client.request(`/orgs/${orgId}/members/${userId}/app-config?app=${app}`),
  setMemberConfig: (orgId, userId, body) => client.request(`/orgs/${orgId}/members/${userId}/app-config`, { method: 'PUT', body: JSON.stringify(body) }),
};

// --- Escalation policies (Respond) ---
// Task 17 (the incident detail page / RaiseIncidentDialog) only needed
// LIST — the manual-raise dialog's optional escalation-policy override
// picker. `crud('/escalation-policies')` already gives list/get/
// create(POST)/update(PUT)/delete for free, matching server/src/incidents/
// escalation-policies.routes.ts's verbs exactly. Task 18
// (EscalationPolicyEditor.jsx) adds `preview` — POST /escalation-policies/
// preview, spec risk 4's per-level spend estimate — writes nothing.
export const escalationPolicies = {
  ...crud('/escalation-policies'),
  preview: (levels) =>
    client.request('/escalation-policies/preview', {
      method: 'POST',
      body: JSON.stringify({ levels }),
    }),
};

// --- Device photos (legacy top-level binding) ---
export const devicePhotos = {
  list: (deviceId) => client.request(`/devices/${deviceId}/photos`),
  create: (deviceId, data) =>
    client.request(`/devices/${deviceId}/photos`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (deviceId, photoId) =>
    client.request(`/devices/${deviceId}/photos/${photoId}`, { method: 'DELETE' }),
};

// --- WebSocket auth tickets (single-use; keeps the JWT out of the WS URL) ---
export const ws = {
  // POST /api/ws-ticket → { ticket }. Redeemed once on the WS handshake.
  ticket: () => client.request('/ws-ticket', { method: 'POST' }),
};

// --- App config (WhatsApp settings, etc.) ---
export const appConfig = {
  get: (keys) => {
    const s = keys ? `?keys=${encodeURIComponent(keys.join(','))}` : '';
    return client.request(`/app-config${s}`);
  },
  set: (key, value) =>
    client.request('/app-config', {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    }),
};

// --- Sultan: CRM API service tokens ---
export const sultanApiTokens = {
  list: () => client.request('/sultan/service-tokens'),
  issue: (body) =>
    client.request('/sultan/service-tokens', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revoke: (id) =>
    client.request(`/sultan/service-tokens/${id}/revoke`, { method: 'POST' }),
};

// --- Sultan: multi-server registry + directory (migration 053/054) ---
export const sultanServers = {
  list: () => client.request('/sultan/servers'),
  create: (body) =>
    client.request('/sultan/servers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) =>
    client.request(`/sultan/servers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/sultan/servers/${id}`, { method: 'DELETE' }),
  check: (id) => client.request(`/sultan/servers/${id}/check`, { method: 'POST' }),
  identities: (id) => client.request(`/sultan/servers/${id}/identities`),
  addIdentity: (id, identifier) =>
    client.request(`/sultan/servers/${id}/identities`, {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    }),
  removeIdentity: (id, identifier) =>
    client.request(`/sultan/servers/${id}/identities/${encodeURIComponent(identifier)}`, {
      method: 'DELETE',
    }),
};

// --- Reseller-admin surface (spec 2026-07-11). impersonate reuses the sultan
// JWT-swap endpoint — the guard server-side allows owning reseller admins. ---
export const reseller = {
  overview: () => client.request('/reseller/overview'),
  createOrg: (body) =>
    client.request('/reseller/orgs', { method: 'POST', body: JSON.stringify(body) }),
  updateOrg: (id, body) =>
    client.request(`/reseller/orgs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setExpiry: (id, expiresAt) =>
    client.request(`/reseller/orgs/${id}/expiry`, { method: 'PATCH', body: JSON.stringify({ expiresAt }) }),
  impersonate: (orgId) =>
    client.request(`/orgs/${orgId}/impersonate`, { method: 'POST' }),
};

// --- Enterprise view (read-only, across the companies a reseller owns). ---
// enter/exit swap the JWT (POST /api/auth/enterprise[/exit] → { token }); the
// caller sets it and rebuilds the session (features/enterprise/useEnterpriseMode).
const enterpriseGet = (path, params) => {
  const qs = queryString(params);
  return client.request(`/enterprise/${path}${qs ? `?${qs}` : ''}`);
};

export const enterprise = {
  enter: () => client.request('/auth/enterprise', { method: 'POST' }),
  exit: () => client.request('/auth/enterprise/exit', { method: 'POST' }),
  overview: () => enterpriseGet('overview'),
  dashboard: () => enterpriseGet('dashboard'),
  devices: (p) => enterpriseGet('devices', p),
  positions: (p) => enterpriseGet('positions', p),
  groups: (p) => enterpriseGet('groups', p),
  vehicles: (p) => enterpriseGet('vehicles', p),
  reports: {
    trips: (p) => enterpriseGet('reports/trips', p),
    stops: (p) => enterpriseGet('reports/stops', p),
    summary: (p) => enterpriseGet('reports/summary', p),
    idling: (p) => enterpriseGet('reports/idling', p),
    alerts: (p) => enterpriseGet('reports/alerts', p),
  },
  events: (p) => enterpriseGet('rules/events', p),
  // Enterprise API keys (owned by the reseller). Same positional shape as the
  // org `apiKeys` client so the Settings → API tabs reuse it; `_orgId` is ignored.
  apiKeys: {
    list: () => client.request('/reseller/api-keys'),
    create: (_orgId, body) =>
      client.request('/reseller/api-keys', { method: 'POST', body: JSON.stringify(body) }),
    revoke: (_orgId, keyId) => client.request(`/reseller/api-keys/${keyId}`, { method: 'DELETE' }),
    usage: (_orgId, days = 30) => client.request(`/reseller/api-keys/usage?days=${days}`),
  },
};

export const sultanResellers = {
  list: () => client.request('/sultan/resellers'),
  create: (body) =>
    client.request('/sultan/resellers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) =>
    client.request(`/sultan/resellers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/sultan/resellers/${id}`, { method: 'DELETE' }),
  addMember: (id, body) =>
    client.request(`/sultan/resellers/${id}/members`, { method: 'POST', body: JSON.stringify(body) }),
  removeMember: (id, userId) =>
    client.request(`/sultan/resellers/${id}/members/${userId}`, { method: 'DELETE' }),
  setOrgs: (id, body) =>
    client.request(`/sultan/resellers/${id}/orgs`, { method: 'PATCH', body: JSON.stringify(body) }),
  listApiKeys: (id) => client.request(`/sultan/resellers/${id}/api-keys`),
  revokeApiKey: (id, keyId) =>
    client.request(`/sultan/resellers/${id}/api-keys/${keyId}`, { method: 'DELETE' }),
};

export const sultanTeam = {
  list: () => client.request('/sultan/team'),
  add: (body) => client.request('/sultan/team', { method: 'POST', body: JSON.stringify(body) }),
  update: (userId, body) =>
    client.request(`/sultan/team/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (userId) => client.request(`/sultan/team/${userId}`, { method: 'DELETE' }),
};

// --- Support bridge (customer → external service desk; spec 2026-07-10) ---
export const support = {
  config: () => client.request('/support/config'),
  create: (body) =>
    client.request('/support/tickets', { method: 'POST', body: JSON.stringify(body) }),
  list: () => client.request('/support/tickets'),
  get: (id) => client.request(`/support/tickets/${encodeURIComponent(id)}`),
  comment: (id, content) =>
    client.request(`/support/tickets/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
};

// --- Sultan: backup status (read-only, migration 055) ---
export const sultanBackups = {
  get: () => client.request('/sultan/backups'),
};

// --- Sultan: GPSgate importer (server: /api/sultan/imports) ---
export const sultanImports = {
  status: () => client.request('/sultan/imports/gpsgate/status'),
  test: () => client.request('/sultan/imports/gpsgate/test', { method: 'POST', timeoutMs: 30_000 }),
  applications: () => client.request('/sultan/imports/gpsgate/applications', { timeoutMs: 60_000 }),
  preview: (body) => client.request('/sultan/imports/preview', { method: 'POST', timeoutMs: 300_000, body: JSON.stringify(body) }),
  create: (body) => client.request('/sultan/imports', { method: 'POST', timeoutMs: 300_000, body: JSON.stringify(body) }),
  list: () => client.request('/sultan/imports'),
  get: (id) => client.request(`/sultan/imports/${id}`),
  pause: (id) => client.request(`/sultan/imports/${id}/pause`, { method: 'POST' }),
  resume: (id, body) => client.request(`/sultan/imports/${id}/resume`, { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) }),
  cancel: (id) => client.request(`/sultan/imports/${id}/cancel`, { method: 'POST' }),
  rollback: (id, confirmName) => client.request(`/sultan/imports/${id}/rollback`, { method: 'POST', timeoutMs: 600_000, body: JSON.stringify({ confirmName }) }),
  // Authed binary (same pattern as staffAttendance.photo).
  quarantineCsv: async (id) => {
    const token = getAuthToken();
    const res = await fetch(`/api/sultan/imports/${id}/quarantine.csv`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      if (res.status === 401) setAuthToken(null);
      throw new Error(String(res.status));
    }
    return res.blob();
  },
};

// --- Sultan: GPSwox CSV history importer (server: /api/sultan/imports/csv) ---
const CSV = '/sultan/imports/csv';
export const sultanCsvImports = {
  createBatch: (body) => client.request(`${CSV}/batches`, { method: 'POST', body: JSON.stringify(body) }),
  getBatch: (id) => client.request(`${CSV}/batches/${id}`),
  devices: (id) => client.request(`${CSV}/batches/${id}/devices`),
  // XHR (not fetch) so upload progress is observable. Resolves the parsed
  // FileView; rejects with an Error carrying { status, code, body } (the shape
  // importFormat.errorText reads).
  uploadFile: (id, file, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api${CSV}/batches/${id}/files`);
      const token = getAuthToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      const fail = (status, body) => {
        if (status === 401) setAuthToken(null);
        const err = new Error(body?.message ?? body?.error ?? `HTTP ${status}`);
        err.status = status;
        err.code = body?.error ?? body?.code ?? null;
        err.body = body;
        reject(err);
      };
      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
      }
      xhr.onerror = () => fail(0, null);
      xhr.onabort = () => fail(0, null);
      xhr.onload = () => {
        let body = null;
        try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { body = null; }
        if (xhr.status >= 200 && xhr.status < 300) resolve(body);
        else fail(xhr.status, body);
      };
      const form = new FormData();
      form.append('file', file);
      xhr.send(form);
    }),
  removeFile: (id, fileId) => client.request(`${CSV}/batches/${id}/files/${fileId}`, { method: 'DELETE' }),
  setFile: (id, fileId, body) => client.request(`${CSV}/batches/${id}/files/${fileId}`, { method: 'PUT', body: JSON.stringify(body) }),
  preview: (id) => client.request(`${CSV}/batches/${id}/preview`, { method: 'POST', timeoutMs: 300_000, body: JSON.stringify({}) }),
  run: (id, body) => client.request(`${CSV}/batches/${id}/run`, { method: 'POST', timeoutMs: 300_000, body: JSON.stringify(body ?? {}) }),
};

// --- Modules SKU layer (migration 057) ---
// Sultan manages the catalog; sultan grants/revokes per-org entitlements.
export const modules = {
  // Any authed user: readable catalog (active + coming_soon).
  catalog: () => client.request('/modules'),
  // Sultan-only: this box's provider-licensed feature keys ({licensed, all}).
  licensedFeatures: () => client.request('/sultan/licensed-features'),
  // Sultan-only: catalog CRUD.
  sultanCreate: (body) =>
    client.request('/sultan/modules', { method: 'POST', body: JSON.stringify(body) }),
  sultanUpdate: (id, body) =>
    client.request(`/sultan/modules/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  sultanDelete: (id) => client.request(`/sultan/modules/${id}`, { method: 'DELETE' }),
  // Sultan + org-admin: per-org entitlement read.
  orgList: (orgId) => client.request(`/orgs/${orgId}/modules`),
  // Sultan-only: grant (upsert) or update an org's module entitlement.
  grant: (orgId, moduleId, body) =>
    client.request(`/orgs/${orgId}/modules/${moduleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  // Sultan-only: revoke an org's module entitlement.
  revoke: (orgId, moduleId) =>
    client.request(`/orgs/${orgId}/modules/${moduleId}`, { method: 'DELETE' }),
  // Sultan-only: recompute materialized flags after editing featureOverrides.
  recompute: (orgId) =>
    client.request(`/orgs/${orgId}/modules/recompute`, { method: 'POST' }),
  // Sultan-only: every org's active module slugs in one call — the Customer
  // Modules page's org-list module counts + "has module" filter. Orgs with
  // no active grants are simply absent (the page treats absent as []).
  orgModulesAll: () => client.request('/sultan/org-modules'),
};

// --- Module templates (sultan-only) ---
// A saved set of modules + an optional default duration, applied to a
// customer in one click. Apply is ADDITIVE (never revokes) and SNAPSHOT
// (editing a template later doesn't touch orgs it was applied to).
export const moduleTemplates = {
  list: () => client.request('/sultan/module-templates'),
  create: (body) =>
    client.request('/sultan/module-templates', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) =>
    client.request(`/sultan/module-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/sultan/module-templates/${id}`, { method: 'DELETE' }),
  // → { granted: slug[], skipped: [{ slug, reason }] }
  apply: (orgId, templateId) =>
    client.request(`/orgs/${orgId}/modules/apply-template`, {
      method: 'POST',
      body: JSON.stringify({ templateId }),
    }),
};

// Organization templates (TASK-294): plan/quotas + module bundle + starter
// content from a model org. Content changes only via `resnapshot`.
export const orgTemplates = {
  list: () => client.request('/sultan/org-templates'),
  get: (id) => client.request(`/sultan/org-templates/${id}`),
  create: (body) => client.request('/sultan/org-templates', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/sultan/org-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/sultan/org-templates/${id}`, { method: 'DELETE' }),
  // → template row + { report: { included, skipped } }
  resnapshot: (id, sourceOrgId) =>
    client.request(`/sultan/org-templates/${id}/resnapshot`, { method: 'POST', body: JSON.stringify(sourceOrgId ? { source_org_id: sourceOrgId } : {}) }),
};

// --- Map data layers (aircraft / liveuamap / weather overlays) ---
export const layers = {
  status: () => client.request('/layers/status'),
  aircraft: () => client.request('/layers/aircraft'),
  liveuamap: () => client.request('/layers/liveuamap'),
  track: (fr24Id) => client.request(`/layers/aircraft/track/${encodeURIComponent(fr24Id)}`),
  fr24Usage: () => client.request('/layers/fr24-usage'),
  // Weather + AQI bundle for a point (weather chip / panel, Task 5). Raster
  // tile URLs are built inline by MapView — no client helper needed there,
  // since MapLibre fills in {z}/{x}/{y} itself.
  weatherPoint: (lat, lon, opts = {}) =>
    client.request(`/layers/weather/point?lat=${lat}&lon=${lon}${opts.forecast ? '&forecast=1' : ''}${opts.lang ? `&lang=${opts.lang}` : ''}`),
};

// ───────────────────────────────────────────────────────────────────────────
// Trackizy Staff app — Plans 1 + 2 backend
// ───────────────────────────────────────────────────────────────────────────

// Staff users (org members with role='staff')
export const staff = {
  list: (params) => client.request(`/staff${qs(params)}`),
  get: (id) => client.request(`/staff/${id}`),
  create: (data) =>
    client.request('/staff', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    client.request(`/staff/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => client.request(`/staff/${id}`, { method: 'DELETE' }),
  // Triggers Plan 1's request-otp under the hood, sending the staff member an SMS.
  sendInvite: (phone) =>
    client.request('/auth/staff/request-otp', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),
};

// Staff groups — org-defined groups of PEOPLE (not Traccar device groups).
export const staffGroups = {
  list: () => client.request('/staff/groups'),
  create: (data) => client.request('/staff/groups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => client.request(`/staff/groups/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => client.request(`/staff/groups/${id}`, { method: 'DELETE' }),
  // Bulk membership from the People roster: { add?: userId[], remove?: userId[] }.
  setMembers: (id, data) => client.request(`/staff/groups/${id}/members`, { method: 'POST', body: JSON.stringify(data) }),
};

// Shifts (Plan 2)
export const staffShifts = {
  start: (body = {}) =>
    client.request('/staff/shifts/start', { method: 'POST', body: JSON.stringify(body) }),
  end: (body = {}) =>
    client.request('/staff/shifts/end', { method: 'POST', body: JSON.stringify(body) }),
  current: () => client.request('/staff/shifts/current'),
};

// Attendance (Plan 2; Phase 2 spec 2026-09-16-staff-schedules-attendance-design.md §5).
// list/summary/create/update are admin/manager only; clockIn/clockOut/current are the caller's own.
export const staffAttendance = {
  clockIn: (body = {}) =>
    client.request('/staff/attendance/clock-in', { method: 'POST', body: JSON.stringify(body) }),
  clockOut: (body = {}) =>
    client.request('/staff/attendance/clock-out', { method: 'POST', body: JSON.stringify(body) }),
  current: () => client.request('/staff/attendance/current'),
  list: (params) => client.request(`/staff/attendance${qs(params)}`),
  summary: (params) => client.request(`/staff/attendance/summary${qs(params)}`),
  create: (body) => client.request('/staff/attendance', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/staff/attendance/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Authed binary: an <img src> cannot send the bearer token, so callers fetch it into a blob URL.
  photo: async (id, kind) => {
    const token = getAuthToken();
    const res = await fetch(`/api/staff/attendance/${encodeURIComponent(id)}/photo/${encodeURIComponent(kind)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // Same 401 handling as client.request (api-client consumeResponse): an expired session clears the token.
      if (res.status === 401) setAuthToken(null);
      throw new Error(String(res.status));
    }
    return res.blob();
  },
};

// Staff schedules (spec 2026-09-16-staff-schedules-attendance-design.md) — admin/manager only.
export const shiftTemplates = {
  list: () => client.request('/staff/shift-templates'),
  create: (body) => client.request('/staff/shift-templates', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/staff/shift-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/staff/shift-templates/${id}`, { method: 'DELETE' }),
};
export const shiftAssignments = {
  list: () => client.request('/staff/shift-assignments'),
  create: (body) => client.request('/staff/shift-assignments', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => client.request(`/staff/shift-assignments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => client.request(`/staff/shift-assignments/${id}`, { method: 'DELETE' }),
};

// Phone-as-tracker opt-in/out (Plan 2)
export const staffTracking = {
  optIn: () => client.request('/staff/tracking/opt-in', { method: 'POST', body: '{}' }),
  optOut: () => client.request('/staff/tracking/opt-out', { method: 'POST', body: '{}' }),
};

// Staff position ingest (Phase 2) + roster query (Phase 3 — People tab + Live Map staff layer)
export const staffPositions = {
  create: (body) => client.request('/staff/positions', { method: 'POST', body: JSON.stringify(body) }),
  latest: () => client.request('/staff/positions/latest'),
  history: ({ from, to, userId } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (userId) params.set('userId', userId);
    return client.request(`/staff/positions/history?${params.toString()}`);
  },
  sosHistory: ({ from, to, userId } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (userId) params.set('userId', userId);
    return client.request(`/staff/positions/sos/history?${params.toString()}`);
  },
  checkinHistory: ({ from, to, userId } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (userId) params.set('userId', userId);
    return client.request(`/staff/positions/checkins/history?${params.toString()}`);
  },
};

// My-work aggregator (Plan 2)
export const staffMyWork = {
  list: () => client.request('/staff/me/work'),
};

// --- Automation: webhook endpoints + delivery log (migration 074) ---
// Admin-strict server-side (server/src/automation/automation.routes.ts's
// requireStrictAdmin) — a webhook aims an HTTP request from inside the
// server's network, so this is not offered to managers.
export const webhooks = {
  listEndpoints: () => client.request('/webhooks/endpoints'),
  createEndpoint: (body) =>
    client.request('/webhooks/endpoints', { method: 'POST', body: JSON.stringify(body) }),
  updateEndpoint: (id, body) =>
    client.request(`/webhooks/endpoints/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEndpoint: (id) => client.request(`/webhooks/endpoints/${id}`, { method: 'DELETE' }),
  rotateSecret: (id) => client.request(`/webhooks/endpoints/${id}/rotate-secret`, { method: 'POST' }),
  testEndpoint: (id) => client.request(`/webhooks/endpoints/${id}/test`, { method: 'POST' }),
  listDeliveries: (params) => client.request(`/webhooks/deliveries${qs(params)}`),
  redeliver: (id) => client.request(`/webhooks/deliveries/${id}/redeliver`, { method: 'POST' }),
};

// --- Automation SP2: rule command actions (migration 089) — /api/rule-commands ---
// Admin/manager server-side, and both mutating routes additionally require the
// App Profile `commands.send` capability — an approver must be somebody who
// could have sent the command by hand
// (server/src/automation/command-actions.routes.ts).
//
// `list` is cursor-paginated: pass the previous page's `nextCursor` back as
// `before`. That value is an OPAQUE base64url blob, not a row id — never parse
// one, never build one.
//
// `approve` rejects with an `ApiError` whose `body` carries the live interlock
// reading on a 409 (`vehicle_moving` / `device_offline` → `speedKmh`, `stale`,
// `online`). Feed it through `approveErrorToState`
// (src/features/alerts/commandApprovalModel.js) rather than reading `err.body`
// at the call site.
export const ruleCommands = {
  list: (params) => client.request(`/rule-commands${qs(params)}`),
  summary: () => client.request('/rule-commands/summary'),
  get: (id) => client.request(`/rule-commands/${id}`),
  // `?? {}` and not a bare `JSON.stringify(body)`: `JSON.stringify(undefined)`
  // is `undefined`, which drops the Content-Type header the client only adds
  // for a string body.
  approve: (id, body) =>
    client.request(`/rule-commands/${id}/approve`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  refuse: (id, body) =>
    client.request(`/rule-commands/${id}/refuse`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
};

// --- Per-org AI provider credentials (Settings → Organization → AI) ---
// Strict org-admin only server-side. The key itself is never returned by any
// of these — GET responds with `hasKey`/`last4` per feature, always both
// entries even with no key configured. The test route spends the customer's
// money (a real provider round-trip), hence the longer timeout.
export const aiCredentials = {
  list: () => client.request('/ai-credentials'),
  set: (feature, body) => client.request(`/ai-credentials/${feature}`, {
    method: 'PUT', body: JSON.stringify(body),
  }),
  remove: (feature) => client.request(`/ai-credentials/${feature}`, { method: 'DELETE' }),
  test: (feature) => client.request(`/ai-credentials/${feature}/test`, {
    method: 'POST', body: '{}', timeoutMs: 30_000,
  }),
};


// API Access module: org-scoped key management (admin only).
export const apiKeys = {
  list: (orgId) => client.request(`/orgs/${orgId}/api-keys`),
  create: (orgId, body) =>
    client.request(`/orgs/${orgId}/api-keys`, { method: 'POST', body: JSON.stringify(body) }),
  revoke: (orgId, keyId) => client.request(`/orgs/${orgId}/api-keys/${keyId}`, { method: 'DELETE' }),
  usage: (orgId, days = 30) => client.request(`/orgs/${orgId}/api-keys/usage?days=${days}`),
};
